import {
  useMutation,
  useQuery,
  type QueryKey,
  useQueryClient
} from "@tanstack/react-query";
import { ERRORS } from "../consts";
import {
  apiAdapter,
  fetcherFactory
} from "../services";
import { useAuthStore } from "../stores";
import { getEnvironmentVariables } from "../utils/general";

export type NotificationResponse = {
  messageList: Notification[]
}

export type Notification = {
  createdDate: string
  description: string
  eventName:string
  eventSentId: number
  isRead: string
  isSent: string
  outputChannel: string
  sendDate: string
  title: string
  userId: string
}

const { basePath } = getEnvironmentVariables();

export function useNotificationMutation() {
  const queryClient = useQueryClient();
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  // const corporateId = useAuthStore(state => state.userData?.organization_id) ?? "";
  // const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  // const learnerId = useAuthStore(state => state.userData?.id_user) ?? "";

  // const refreshSession = useAuthStore(state => state.refreshSession);
  const fetcher = fetcherFactory();
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  
  const uri = "/notificator/messages";
  // const params = `corporateId${corporateId}&learnerId${learnerId}&initiativeId=${initiativeId}$`;

  const uriGet = "/notificator/messages";
  const paramsGet1 = "outputChannel=WEB";
  const paramsGet2 = "outputChannel=WEB&readStatus=Y";
  const paramsGet3 = "outputChannel=WEB&readStatus=N";
  
  const keyAll = [ uriGet, paramsGet1, isSeedingSession ];
  const keyRead = [ uriGet, paramsGet2, isSeedingSession ];
  const keyUnread = [ uriGet, paramsGet3, isSeedingSession ];

  return useMutation({
    mutationFn: ({
      messagesId,
      status
    } : {
      messagesId: number | number[]
      status: "Y" | "N"
    }) => fetcher(
      // `${basePath}${uri}?${params}`,
      `${basePath}${uri}`,
      {
        body: JSON.stringify({
          "messageIds": Array.isArray(messagesId) ? messagesId : [messagesId],
          "readStatus": status
        }),
        headers:{
          "authorization": idToken,
          "x-ada-session-token": sessionToken,

          "content-type":"application/json"
        },
        method: "POST"
      })
      .catch((error) => {

        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }),
    onMutate: async ({ messagesId, status }) => {
      const ids = new Set(Array.isArray(messagesId) ? messagesId : [messagesId]);

      await Promise.all([
        queryClient.cancelQueries({ queryKey: keyAll }),
        queryClient.cancelQueries({ queryKey: keyRead }),
        queryClient.cancelQueries({ queryKey: keyUnread })
      ]);

      const previous = {
        all: queryClient.getQueryData<Notification[]>(keyAll),
        read: queryClient.getQueryData<Notification[]>(keyRead),
        unread: queryClient.getQueryData<Notification[]>(keyUnread)
      };

      if (previous.all) {
        queryClient.setQueryData<Notification[]>(
          keyAll,
          previous.all.map(n => (ids.has(n.eventSentId) ? { ...n, isRead: status } : n))
        );
      }
      if (previous.read) {
        const updated = status === "Y"
          ? [
            ...(previous.unread?.filter(n => ids.has(n.eventSentId)).map(n => ({ ...n, isRead: "Y" })) ?? []),
            ...previous.read
          ]
          : previous.read.filter(n => !ids.has(n.eventSentId));
        queryClient.setQueryData<Notification[]>(keyRead, updated);
      }
      if (previous.unread) {
        const updated = status === "Y"
          ? previous.unread.filter(n => !ids.has(n.eventSentId))
          : [
            ...(previous.read?.filter(n => ids.has(n.eventSentId)).map(n => ({ ...n, isRead: "N" })) ?? []),
            ...previous.unread
          ];
        queryClient.setQueryData<Notification[]>(keyUnread, updated);
      }

      return previous;
    },
    onError: (_err, _vars, context) => {
      if (!context) return;
      if (context.all !== undefined) queryClient.setQueryData(keyAll, context.all);
      if (context.read !== undefined) queryClient.setQueryData(keyRead, context.read);
      if (context.unread !== undefined) queryClient.setQueryData(keyUnread, context.unread);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: keyAll });
      queryClient.invalidateQueries({ queryKey: keyRead });
      queryClient.invalidateQueries({ queryKey: keyUnread });
    }
  });
}

export function useNotificationQuery({
  enabled=true,
  queryKey=[],
  readStatus
} : {
  enabled?: boolean,
  queryKey?: string[]
  readStatus?: "Y" | "N"
}) {
  const idToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken()) ?? "";
  const initiativeId = useAuthStore(state => state.userData?.initiative_id) ?? "";
  const sessionToken = useAuthStore(state => state.sessionToken) ?? "";
  const setUnauthorized = useAuthStore(state => state.setUnAuthorized);
  const isSeedingSession = useAuthStore(state => state.hasSeedSession);
  const fetcher = fetcherFactory();

  const uri = "/notificator/messages";
  const params = `outputChannel=WEB${readStatus ? `&readStatus=${readStatus}` : ""}`;

  return useQuery<
    unknown,
    unknown,
    Notification[],
    QueryKey
  >({
    enabled: Boolean(enabled && sessionToken && idToken && initiativeId) && !isSeedingSession,
    queryFn: () => fetcher(
      `${basePath}${uri}?${params}`,
      {
        headers: {
          "authorization": idToken,
          "x-ada-session-token": sessionToken
        }
      })
      .then((res) => {
        return res.json();
      })
      .then((data: NotificationResponse) => {
        return (apiAdapter(data.messageList) as Notification[])?.sort((a, b) => (
          new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime() )
        );
      })
      .catch((error) => {
        if (error === ERRORS.UNAUTHORIZED) {
          setUnauthorized(true);
          throw error; // rethrow so that react query doesn't complain about undefined return value
        } else {
          throw error;
        }
      }),
    queryKey: [ uri, params, isSeedingSession, ...queryKey ]
  });
}
